import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, FileText, Package, X, SlidersHorizontal, Check, Building2, ListPlus } from "lucide-react";
import { productAPI, BASE_URL } from "../services/api";
import { useQuoteCart } from "../context";

const getProductImage = (product) => {
  if (product?.image_url) {
    if (product.image_url.startsWith("http")) return product.image_url;
    return `${BASE_URL}${product.image_url}`;
  }
  return "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=300&q=80";
};

const Products = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const typeParam = searchParams.get("type");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [typesMap, setTypesMap] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!loading && categories.length > 0) {
      if (categoryParam) {
        const catId = parseInt(categoryParam);
        const found = categories.find(c => c.id === catId);
        if (found) {
          setActiveCategory(catId);
          const types = typesMap[catId] || [];
          if (typeParam) {
            const tid = parseInt(typeParam);
            setActiveType(types.find(t => t.id === tid) ? tid : (types[0]?.id || null));
          } else {
            setActiveType(types[0]?.id || null);
          }
        }
      }
    }
  }, [loading, categoryParam, typeParam, categories, typesMap]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        productAPI.getProducts({ limit: 500 }),
        productAPI.getCategories()
      ]);
      const allProducts = prodRes.data?.data?.products || prodRes.data?.data || [];
      const allCats = (catRes.data?.data || []).filter(c =>
        allProducts.some(p => p.category_id === c.id)
      );

      const tMap = {};
      await Promise.all(
        allCats.filter(c => c.has_type).map(async c => {
          try {
            const r = await productAPI.getTypesByCategory(c.id);
            tMap[c.id] = r.data?.data || [];
          } catch { tMap[c.id] = []; }
        })
      );

      setProducts(allProducts);
      setCategories(allCats);
      setTypesMap(tMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    if (catId === null) { setActiveType(null); return; }
    const types = typesMap[catId] || [];
    setActiveType(types.length > 0 ? types[0].id : null);
  };

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); };

  const filtered = products.filter(p => {
    const matchesCat = activeCategory === null || p.category_id === activeCategory;
    const matchesType = !activeType || p.type_id === activeType;
    const matchesSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesType && matchesSearch;
  });

  const activeCat = categories.find(c => c.id === activeCategory);
  const activeTypes = activeCategory ? (typesMap[activeCategory] || []) : [];

  const groupedByCategory = activeCategory === null
    ? categories.map(cat => ({
        cat,
        items: filtered.filter(p => p.category_id === cat.id)
      })).filter(g => g.items.length > 0)
    : null;

  return (
    <div className="min-h-screen bg-[#f5f5f0] pt-32 pb-16">

      {/* Page Header */}
      <div className="bg-white border-b border-primary-100 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold text-primary-800">Products</h1>
              {!loading && (
                <p className="text-sm text-primary-400 mt-0.5">
                  <span className="font-semibold text-secondary-600">{filtered.length}</span> product{filtered.length !== 1 ? 's' : ''}
                  {activeCat ? <> in <span className="font-medium text-primary-700">{activeCat.name}</span></> : ' across all categories'}
                  {search && <> matching "<span className="font-medium text-primary-700">{search}</span>"</>}
                </p>
              )}
            </div>
            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full sm:max-w-sm">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-300" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-primary-200 rounded-xl bg-primary-50 focus:outline-none focus:ring-2 focus:ring-secondary-400 focus:bg-white transition-colors text-primary-700 placeholder-primary-300"
                />
                {searchInput && (
                  <button type="button" onClick={() => { setSearchInput(""); setSearch(""); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-primary-300 hover:text-primary-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button type="submit" className="px-4 py-2.5 bg-secondary-600 text-white text-sm font-semibold rounded-xl hover:bg-secondary-700 transition-colors shadow-sm">
                Search
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Filter Bar */}
        {!loading && categories.length > 0 && (
          <div className="bg-white rounded-2xl border border-primary-100 shadow-sm p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <SlidersHorizontal className="w-4 h-4 text-primary-300" />
              <span className="text-xs font-bold text-primary-400 uppercase tracking-wide">Filter by Category</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === null
                    ? "bg-primary-700 text-white shadow-md"
                    : "bg-primary-50 text-primary-600 hover:bg-secondary-50 hover:text-secondary-700"
                }`}
              >
                All
                <span className={`ml-1.5 text-[10px] font-bold ${activeCategory === null ? "opacity-70" : "text-primary-400"}`}>
                  ({products.length})
                </span>
              </button>
              {categories.map(cat => {
                const count = products.filter(p => p.category_id === cat.id).length;
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                      isActive
                        ? "bg-primary-700 text-white shadow-md"
                        : "bg-primary-50 text-primary-600 hover:bg-secondary-50 hover:text-secondary-700"
                    }`}
                  >
                    {cat.name}
                    <span className={`ml-1.5 text-[10px] font-bold ${isActive ? "opacity-70" : "text-primary-400"}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Type sub-tabs */}
            {activeTypes.length > 0 && (
              <div className="mt-3 pt-3 border-t border-primary-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-primary-300 uppercase tracking-wide">Type:</span>
                {activeTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setActiveType(type.id)}
                    className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                      activeType === type.id
                        ? "bg-secondary-100 text-secondary-700 border border-secondary-300"
                        : "bg-primary-50 text-primary-500 border border-transparent hover:border-primary-200 hover:text-primary-700"
                    }`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-primary-100 aspect-square" />
                <div className="p-3">
                  <div className="h-3 bg-primary-100 rounded w-3/4 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-primary-100 shadow-sm">
            <Package className="w-12 h-12 mx-auto mb-3 text-primary-200" />
            <p className="text-primary-500 font-medium mb-1">No products found</p>
            {search && <p className="text-sm text-primary-300 mb-4">No results for "{search}"</p>}
            {search && (
              <button onClick={() => { setSearch(""); setSearchInput(""); }}
                className="text-sm text-secondary-600 font-semibold hover:underline">
                Clear search
              </button>
            )}
          </div>
        ) : groupedByCategory ? (
          groupedByCategory.map(({ cat, items }) => (
            <div key={cat.id} className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-secondary-600 rounded-full" />
                  <h2 className="text-lg font-extrabold text-primary-800 uppercase tracking-wide">{cat.name}</h2>
                  <span className="bg-secondary-100 text-secondary-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
                    {items.length}
                  </span>
                </div>
                <button
                  onClick={() => handleCategoryChange(cat.id)}
                  className="text-sm text-secondary-600 hover:text-secondary-700 font-semibold flex items-center gap-1 group"
                >
                  View All <span className="group-hover:translate-x-0.5 transition-transform">→</span>
                </button>
              </div>
              <ProductGrid products={items.slice(0, 8)} />
            </div>
          ))
        ) : (
          <ProductGrid products={filtered} />
        )}
      </div>
    </div>
  );
};

const ProductGrid = ({ products }) => {
  const { addToQuoteCart, isProductInQuoteCart } = useQuoteCart();
  const [justAdded, setJustAdded] = useState({});
  // Brand picker for the product currently being added — null when closed.
  // { product, loading, brands, selectedBrandId, quantity }
  const [picker, setPicker] = useState(null);

  const flashAdded = (productId) => {
    setJustAdded(prev => ({ ...prev, [productId]: true }));
    setTimeout(() => {
      setJustAdded(prev => ({ ...prev, [productId]: false }));
    }, 1500);
  };

  const handleAddToQuote = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    setPicker({ product, loading: true, brands: [], selectedBrandId: null, quantity: 1 });
    try {
      const res = await productAPI.getProductBrands(product.id);
      const { primary = [], secondary = [], other = [] } = res.data?.data || {};
      const brands = [...primary, ...secondary, ...other];

      if (brands.length === 0) {
        // No brand variants for this product — nothing to disambiguate, add directly.
        addToQuoteCart({
          product_id: product.id,
          product_name: product.name,
          product_image: product.image_url,
          unit: product.unit || product.measurement?.symbol || null,
          quantity: 1,
        });
        setPicker(null);
        flashAdded(product.id);
        return;
      }

      setPicker({ product, loading: false, brands, selectedBrandId: brands[0].id, quantity: 1 });
    } catch {
      setPicker(null);
    }
  };

  const confirmAddWithBrand = () => {
    const brand = picker.brands.find(b => b.id === picker.selectedBrandId);
    addToQuoteCart({
      product_id: picker.product.id,
      product_name: picker.product.name,
      product_image: picker.product.image_url,
      unit: picker.product.unit || picker.product.measurement?.symbol || null,
      brand_id: brand?.id || null,
      brand_name: brand?.brand_name || null,
      brand_type: brand?.brand_type || null,
      quantity: parseFloat(picker.quantity) || 1,
    });
    flashAdded(picker.product.id);
    setPicker(null);
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {products.map(product => {
        const added = justAdded[product.id] || isProductInQuoteCart(product.id);
        return (
          <Link
            key={product.id}
            to={`/products/${product.id}`}
            className="group relative bg-white rounded-2xl border border-primary-100 shadow-sm overflow-hidden hover:shadow-xl hover:border-secondary-300 hover:-translate-y-1 transition-all duration-200"
          >
            <button
              onClick={(e) => handleAddToQuote(e, product)}
              title={added ? 'Added to Quote Cart' : 'Add to Quote'}
              className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${
                added ? 'bg-green-600 text-white' : 'bg-white text-primary-600 hover:bg-secondary-600 hover:text-white'
              }`}
            >
              {added ? <Check className="w-3.5 h-3.5" /> : <span className="text-base leading-none">+</span>}
            </button>
            <div className="relative aspect-square overflow-hidden bg-secondary-50">
              <img
                src={getProductImage(product)}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                onError={e => { e.target.src = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200&q=80"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-2 left-0 right-0 flex justify-center translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <span className="inline-flex items-center gap-1 bg-secondary-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                  <FileText className="w-2.5 h-2.5" /> Get Quote
                </span>
              </div>
            </div>
            <div className="px-3 py-2.5 border-t border-primary-50">
              <p className="text-[11px] text-center font-bold text-primary-700 group-hover:text-secondary-600 transition-colors leading-snug uppercase tracking-wide line-clamp-2">
                {product.name}
              </p>
            </div>
          </Link>
        );
      })}

      {/* Brand picker — shown before a product with brand variants is added to the Quote Cart */}
      {picker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => { e.preventDefault(); if (!picker.loading) setPicker(null); }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {picker.loading ? (
              <div className="py-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-primary-800">Select Brand</h3>
                  <button onClick={() => setPicker(null)} className="p-1 hover:bg-primary-50 rounded-lg">
                    <X className="w-4 h-4 text-primary-400" />
                  </button>
                </div>
                <p className="text-xs text-primary-400 mb-3">{picker.product.name}</p>

                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1 mb-4">
                  {picker.brands.map(brand => {
                    const isSelected = picker.selectedBrandId === brand.id;
                    const logoUrl = brand.logo_url
                      ? (brand.logo_url.startsWith('http') ? brand.logo_url : `${BASE_URL}${brand.logo_url}`)
                      : null;
                    return (
                      <label
                        key={brand.id}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected ? 'border-secondary-500 bg-secondary-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="brand"
                          checked={isSelected}
                          onChange={() => setPicker(p => ({ ...p, selectedBrandId: brand.id }))}
                          className="w-4 h-4 accent-secondary-600 flex-shrink-0"
                        />
                        {logoUrl ? (
                          <img src={logoUrl} alt={brand.brand_name} className="w-7 h-7 rounded-lg object-contain bg-white border border-gray-100 p-0.5 flex-shrink-0" />
                        ) : (
                          <div className="w-7 h-7 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-secondary-500" />
                          </div>
                        )}
                        <span className={`text-sm font-semibold ${isSelected ? 'text-secondary-800' : 'text-primary-700'}`}>
                          {brand.brand_name}
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <label className="text-xs font-bold text-primary-600">Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={picker.quantity}
                    onChange={(e) => setPicker(p => ({ ...p, quantity: e.target.value }))}
                    className="w-20 px-2.5 py-1.5 text-sm border border-primary-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                </div>

                <button
                  onClick={confirmAddWithBrand}
                  disabled={!picker.selectedBrandId}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-600 text-white rounded-xl text-sm font-bold hover:bg-secondary-700 disabled:opacity-50 transition-colors"
                >
                  <ListPlus className="w-4 h-4" /> Add to Quote Cart
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
